import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import endpoints from '@endpoints';
import { StationMapper } from './station.mapper';
import { ApiService } from '@services/api/api.service';
import { interpolate } from '@helpers/index';
import { UserInfo } from '@models/user/user-info';
import { StationDetail } from '@models/station/detail';
import { StationLookup } from '@models/station/lookup';
import { PagedResponse, SimpleResponse } from '@models/api.model';
import {
    StationFilterOptions,
    StationFilterOptionsResponse,
} from '@models/station/filter-options';

@Injectable({
    providedIn: 'root',
})
export class StationService {
    private readonly _path = 'v1';

    private readonly api = inject(ApiService);
    private readonly mapper = inject(StationMapper);

    getStations(params = {}) {
        return this.api.get(this._path + endpoints.stations, params, true).pipe(
            map((response) => {
                const pagedResponse = response as PagedResponse<StationDetail>;
                return new PagedResponse<StationDetail>(
                    pagedResponse.pageIndex,
                    pagedResponse.pageSize,
                    pagedResponse.totalItems,
                    pagedResponse.items.map(this.mapper.toStationDetail),
                );
            }),
        );
    }

    getStationsFilterOptions() {
        return this.api.get(this._path + endpoints.stationsFilterOptions).pipe(
            map((response: SimpleResponse<StationFilterOptionsResponse>) =>
                this.mapper.toStationDetailFilterOptions(response.data),
            ),
            catchError(() => of(StationFilterOptions.initial())),
        );
    }

    getStationFixers(id: number): Observable<UserInfo[]> {
        return this.api
            .get(this._path + interpolate(endpoints.stationFixers, { id }))
            .pipe(
                map(
                    (response: SimpleResponse<{ fixers: UserInfo[] }>) =>
                        response.data.fixers,
                ),
                catchError(() => of([])),
            );
    }

    getStationLookup(id: number): Observable<StationLookup | null> {
        return this.api
            .get(this._path + interpolate(endpoints.stationLookup, { id }))
            .pipe(
                map((response: SimpleResponse<StationLookup>) => response.data),
                catchError(() => of(null)),
            );
    }
}
